import { h, ref, Ref } from 'vue';
import { dropdownField } from '@/ui/components/form';
import { Task, useTask, useValidation } from '@/ui/concerns';
import {
  IdentityProviderType,
  oAuthDefaultData,
  samlDefaultData,
  SsoConfiguration,
  validate,
} from '@/entities/sso-configuration';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { OAuthFormFields } from '@/ui/pages/sso/OAuthFormFields';
import { SamlFormFields } from '@/ui/pages/sso/SamlFormFields';
import { filterListToOptions } from '@/ui/helpers';
import { filter as getAllRoles } from '@/services/api/resources/role';
import { box, pageSubtitle } from '@/ui/components';
import { JitFormFields } from '@/ui/pages/sso/JitFormFields';
import { ScimFormFields } from '@/ui/pages/sso/ScimFormFields';

export const IdentityProviderOptions = ref<SelectOptions<string>>([]);
IdentityProviderOptions.value = [
  {
    value: IdentityProviderType.OAUTH2,
    label: t('sso.oauth2'),
  },
  {
    value: IdentityProviderType.SAML,
    label: t('sso.saml'),
  },
];

const getInitialIdentityProviderData = (identityProviderType: string) => {
  switch (identityProviderType) {
    case IdentityProviderType.OAUTH2:
      return oAuthDefaultData;
    case IdentityProviderType.SAML:
      return samlDefaultData;
    default:
      return oAuthDefaultData;
  }
};

export function useSsoConfigurationForm(
  record: Ref<SsoConfiguration>,
  task: Task,
  isScimConfirmationModalRequired: Ref<boolean>,
  isErrorLogsModalOpen: Ref<boolean>,
) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const roleOptions = ref<SelectOptions>([]);
  useTask(
    () => getAllRoles(),
    roles => {
      roles.data.unshift({ id: 0, title: t('sso.default-role') });
      roleOptions.value = filterListToOptions(roles);
    },
  ).run();

  const formFields = () => [
    box([
      pageSubtitle(t('sso.general')),
      dropdownField(
        {
          name: 'identityProviderType',
          label: t('sso.identity-provider-type'),
          tooltipText: t('sso.tooltips.identity-provider-type'),
          value: record.value.identityProviderType || IdentityProviderType.OAUTH2,
          placeholder: t('sso.identity-provider-type'),
          required: true,
          options: IdentityProviderOptions.value,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
        },
        value => {
          if (value !== record.value.identityProviderType) {
            record.value.configuration = getInitialIdentityProviderData(value);
          }
          record.value.identityProviderType = value;
        },
      ),

      record.value.identityProviderType === IdentityProviderType.OAUTH2 && OAuthFormFields(record, validationErrors, task),
      record.value.identityProviderType === IdentityProviderType.SAML && SamlFormFields(record, validationErrors, task),
    ]),

    box([
      pageSubtitle(t('sso.jit.title'), 'sso-jit__title'),
      h('p', { class: 'sso-jit__subtitle' }, [t('sso.jit.subtitle')]),
      JitFormFields(record, validationErrors, task, roleOptions),
    ]),

    box([
      pageSubtitle(t('sso.scim.title'), 'sso-scim__title'),
      h('p', { class: 'sso-scim__subtitle' }, [t('sso.scim.subtitle')]),
      ScimFormFields(record, validationErrors, task, roleOptions, isScimConfirmationModalRequired, isErrorLogsModalOpen),
    ]),
  ];

  return {
    formFields,
    hasNoValidationErrors,
    showValidationErrors,
    handleSubmit,
  };
}
